import { ArrowRightOutlined } from '@ant-design/icons';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { IncidentListItem } from '@/app/services/incident/incident.service';
import { resolveIncidentOwnerTags } from '@/app/components/incident-overview/utils/incident-landlord.utils';
import { IncidentOwnerTagBadge } from '@/app/components/incident-overview/IncidentOwnerTagBadge';
import { SlaTableExpandButton } from '~/app/landing/sla/shared/components/SlaTableExpandButton';
import { Button } from '~/app/components/common/Button/';

type Props = {
  record: IncidentListItem;
  expanded: boolean;
  onExpand: (record: IncidentListItem, event: MouseEvent<HTMLElement>) => void;
  onOpenDetailsAction: (incidentId: string) => void;
};

export function IncidentTableRowControls({
  record,
  expanded,
  onExpand,
  onOpenDetailsAction,
}: Props) {
  const { t } = useTranslation('incidents');
  const canExpand = Boolean(record.omp);
  const ownerTags = resolveIncidentOwnerTags(record.omp?.assets);

  return (
    <div
      className={`incident-table-row-controls${canExpand ? '' : ' incident-table-row-controls--details-only'}`}
    >
      {canExpand ? (
        <SlaTableExpandButton
          expanded={expanded}
          ariaLabel={
            expanded
              ? t('overview.table.collapseImpact', 'Collapse financial impact')
              : t('overview.table.expandImpact', 'Expand financial impact')
          }
          onClick={(event) => onExpand(record, event)}
        />
      ) : null}

      <Button
        type="text"
        rounded
        className="incident-table-row-controls__details"
        title={t('overview.table.openDetails', 'Open incident details')}
        aria-label={t('overview.table.openDetails', 'Open incident details')}
        style={{
          height: 'auto',
          minHeight: 'unset',
          fontWeight: 600,
          letterSpacing: '0.01em',
          boxShadow: 'none',
        }}
        onClick={(event) => {
          event.stopPropagation();
          onOpenDetailsAction(record.id);
        }}
      >
        <ArrowRightOutlined className="incident-table-row-controls__details-icon" />
        <span className="incident-table-row-controls__details-label">
          {t('overview.table.details', 'Details')}
        </span>
      </Button>

      {ownerTags.length ? (
        <div
          className={`incident-table-row-controls__owners${ownerTags.length > 1 ? ' incident-table-row-controls__owners--stacked' : ''}`}
        >
          {ownerTags.map((tag) => (
            <IncidentOwnerTagBadge key={`${tag.kind}-${tag.label}`} tag={tag} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
